import React, { useEffect, useCallback, useMemo } from "react";
import { Intro } from "components/commons";
import locale from "localization";
import { getStations, updateStationStatus } from "apis";
import useDataTable from "hooks/useDataTable";
import { initialFilterState } from "./stations-filter.state";
import { mapDataToList, mapFilterToRequest } from "./stations.mapper";
import { stationsColumn } from "./stations-column";
import DataTableV2 from "components/commons/data-table/data-table-v2";
import styles from "./stations.module.scss";
import StationsFilter from "./stations-filter";
import { useModal, useForm, useApi, useRouter } from "hooks";
import UpdateStationStatusModal from "./station-status-modal/station-status-modal";
import Validation from "utils/validation.utils";
import { PagesKeys, StationStatus } from "enums";
import ConfirmModal from "components/modals/confirm-modal/confirm-modal";
import { Path } from "enums";
import useStations from "hooks/useStations";
import useAuth from "hooks/useAuth";

const StationsModule = () => {
  const router = useRouter();
  const { fetch: getAllStations } = useStations();
  const updateStationStatusModal = useModal();
  const successModal = useModal();
  const { hasPermission, stationCodes } = useAuth({
    pageKey: PagesKeys.STATIONS,
  });

  const updatePromoStatus = useApi({
    api: updateStationStatus,
  });

  const initialState = useMemo(() => {
    return {
      reason: {
        name: "reason",
        value: "",
        placeholder: "Select",
        required: true,
        validations: [Validation.required()],
      },
      remarks: {
        name: "remarks",
        value: "",
        placeholder: locale.typeRemarks,
        rowsMax: 7,
        maxLength: 225,
        validations: [],
        inputDisabled: true,
        disabled: true,
        required: false,
      },
    };
  }, []);

  const updateStationForm = useForm({
    initialState,
  });

  const handleOnClickActionOption = (station, status) => {
    updateStationForm.clearForm();
    updateStationStatusModal.show({
      title: `${
        status === StationStatus.ACTIVE ? locale.activateStation : locale.deactivateStation
      }?`,
      content: (
        <locale.Populate
          text={
            status === StationStatus.ACTIVE
              ? locale.activateStationContent
              : locale.deactivateStationContent
          }
          items={[
            <b>
              <br />
              {status === StationStatus.ACTIVE ? station.name : ""}
            </b>,
          ]}
        />
      ),
      primary: {
        text: status === StationStatus.ACTIVE ? locale.yesActivate : locale.deactivateStation,
      },
      secondary: {
        text: locale.cancel,
      },
      stationId: station?.stationId,
      status: status,
    });
  };

  const { filter, search, table } = useDataTable({
    api: {
      api: getStations,
      params: {
        page: 1,
        perPage: 10,
        orderBy: "createdAt",
        isStationCheck: true,
        stationCodes,
      },
      mapper: (param) => {
        return param;
      },
    },
    filter: { initialState: initialFilterState(), mapper: mapFilterToRequest },
    table: {
      key: "stations",
      mapper: (stations) =>
        mapDataToList({
          stations,
          handleOnClickActionOption,
          router,
          hasModifyAccess: hasPermission({ permissionsList: ["modifyAccess"] }),
        }),
      columns: stationsColumn,
    },
  });

  const handleSubmitUpdateStationStatus = useCallback(async () => {
    const { fields } = updateStationForm;
    await updatePromoStatus.request({
      stationId: updateStationStatusModal.stationId,
      query: {
        status: updateStationStatusModal.status,
        remarks: fields?.remarks?.value ?? "",
        reason: fields?.reason?.value ?? "",
      },
    });

    await getAllStations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateStationStatusModal, updateStationForm, updatePromoStatus]);

  // const handleTabChange = (tab) => {
  //   if (tab) {
  //     let fs = {};

  //     if ([StationStatusEnum.ACTIVE, "offline"].includes(tab.status)) {
  //       fs = {
  //         ...fs,
  //         isConnectedToPts: {
  //           value:
  //             tab.status === StationStatusEnum.ACTIVE
  //               ? ["online"]
  //               : tab.status === "offline"
  //               ? ["offline"]
  //               : [],
  //           isSelectedAll: false,
  //         },
  //         status: initialFilterState().status,
  //       };
  //     } else if (
  //       [
  //         StationStatusEnum.MANUAL_MODE,
  //         StationStatusEnum.PENDING,
  //         StationStatusEnum.DEACTIVATED,
  //       ].includes(tab.status)
  //     ) {
  //       fs = {
  //         ...fs,
  //         status: {
  //           value: tab.status ? [tab.status] : [],
  //           isSelectedAll: false,
  //         },
  //         isConnectedToPts: initialFilterState().isConnectedToPts,
  //       };
  //     }

  //     filter.applyFilter({
  //       ...filter.filterState,
  //       ...fs,
  //     });
  //   } else {
  //     filter.applyFilter({
  //       ...filter.filterState,
  //       isConnectedToPts: initialFilterState().isConnectedToPts,
  //       status: initialFilterState().status,
  //     });
  //   }
  // };

  useEffect(() => {
    if (updatePromoStatus?.called && !updatePromoStatus?.error) {
      updateStationStatusModal.close();
      table?.refetch();
      successModal?.show({
        titleStyle: styles.textNight,
        contentStyle: styles.textNight,
        title: locale.exclamatedSuccess,
        content: (
          <locale.Populate
            text={locale.updateStationStatusSuccessMessage}
            items={[
              <b>
                {updateStationStatusModal.status === StationStatus.ACTIVE
                  ? "activated"
                  : "deactivated"}
              </b>,
            ]}
          />
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePromoStatus?.error, updatePromoStatus?.called]);

  return (
    <>
      <UpdateStationStatusModal
        {...updateStationStatusModal}
        primary={{
          ...updateStationStatusModal?.primary,
          disabled:
            updatePromoStatus?.loading ||
            updateStationStatusModal.status === StationStatus.DEACTIVATED
              ? !updateStationForm?.isFormSubmittable
              : false,
          onClick: handleSubmitUpdateStationStatus,
          loading: updatePromoStatus?.loading,
        }}
        closable={!updatePromoStatus?.loading}
        form={updateStationForm}
      />
      <ConfirmModal {...successModal} />
      <Intro title={locale.stations} />
      {/* <StationTabs onChange={handleTabChange} /> */}
      <StationsFilter
        {...filter}
        {...search}
        actionText={locale.addStation}
        actionOnClick={() => router.push(Path.AddStation)}
      />

      <div className={styles.table}>
        <DataTableV2 {...table} />
      </div>
    </>
  );
};

export default StationsModule;
